import React, { useEffect, useMemo, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import api from "../../services/api";

import { i18n } from "../../translate/i18n";
import {
    Box,
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    textRight: {
        textAlign: "right",
    },
    tabPanelsContainer: {
        padding: theme.spacing(2),
    },
    sectionTitle: {
        marginBottom: theme.spacing(1),
    },
    helperText: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
    tableInput: {
        maxWidth: 150,
    },
}));

const legacyDefaults = {
    messageInterval: 20,
    longerIntervalAfter: 20,
    greaterInterval: 60,
};

const toPositiveInteger = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }

    return parsed;
};

const buildLegacyIntervalRanges = (settings) => {
    const messageInterval = toPositiveInteger(settings.messageInterval) || legacyDefaults.messageInterval;
    const greaterInterval = toPositiveInteger(settings.greaterInterval) || legacyDefaults.greaterInterval;
    const longerIntervalAfter = toPositiveInteger(settings.longerIntervalAfter) || 0;

    const firstRange = {
        limit: longerIntervalAfter > 0 ? longerIntervalAfter : null,
        min: Math.max(1, Math.floor(messageInterval / 2)),
        max: messageInterval,
    };

    if (longerIntervalAfter <= 0) {
        return [firstRange];
    }

    return [
        firstRange,
        {
            limit: null,
            min: Math.max(1, Math.floor(greaterInterval / 2)),
            max: greaterInterval,
        },
    ];
};

const initialSettings = {
    ...legacyDefaults,
    variables: [],
    intervalRanges: buildLegacyIntervalRanges(legacyDefaults),
};

const safeJsonParse = (value, fallback) => {
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const sortRangesForState = (ranges) => {
    return [...ranges].sort((a, b) => {
        if (a.limit === null && b.limit === null) return 0;
        if (a.limit === null) return 1;
        if (b.limit === null) return -1;

        const limitA = toPositiveInteger(a.limit);
        const limitB = toPositiveInteger(b.limit);

        if (limitA === null && limitB === null) return 0;
        if (limitA === null) return 1;
        if (limitB === null) return -1;

        return limitA - limitB;
    });
};

const normalizeIntervalRanges = (ranges) => {
    const errors = [];

    if (!Array.isArray(ranges) || ranges.length === 0) {
        return {
            ranges: [],
            errors: ["Adicione pelo menos uma faixa de intervalo."],
        };
    }

    ranges.forEach((range, index) => {
        if (range.limit === null && index !== ranges.length - 1) {
            errors.push("A faixa sem limite precisa ser a ultima faixa.");
        }
    });

    const orderedRanges = sortRangesForState(ranges);
    const limits = new Set();
    let openEndedCount = 0;
    let previousLimit = 0;

    const normalized = orderedRanges.map((range, index) => {
        const isOpenEnded = range.limit === null;
        const limit = isOpenEnded ? null : toPositiveInteger(range.limit);
        const min = toPositiveInteger(range.min);
        const max = toPositiveInteger(range.max);

        if (isOpenEnded) {
            openEndedCount += 1;
            if (index !== orderedRanges.length - 1) {
                errors.push("A faixa sem limite precisa ser a ultima faixa.");
            }
        }

        if (!isOpenEnded && limit === null) {
            errors.push(`Faixa ${index + 1}: informe um limite inteiro maior que zero.`);
        }

        if (min === null || max === null) {
            errors.push(`Faixa ${index + 1}: os intervalos devem ser inteiros maiores que zero.`);
        }

        if (min !== null && max !== null && min > max) {
            errors.push(`Faixa ${index + 1}: o intervalo minimo nao pode ser maior que o maximo.`);
        }

        if (limit !== null) {
            if (limits.has(limit)) {
                errors.push(`Limite duplicado: ${limit} contatos.`);
            }

            if (limit <= previousLimit) {
                errors.push("Os limites das faixas precisam estar em ordem crescente.");
            }

            limits.add(limit);
            previousLimit = limit;
        }

        return { limit, min, max };
    });

    if (openEndedCount > 1) {
        errors.push("Existe mais de uma faixa final sem limite.");
    }

    return {
        ranges: normalized,
        errors: [...new Set(errors)],
    };
};

const getRangeLabel = (ranges, index) => {
    const previousRange = index > 0 ? ranges[index - 1] : null;
    const previousLimit = previousRange ? toPositiveInteger(previousRange.limit) : null;
    const currentLimit = toPositiveInteger(ranges[index].limit);
    const start = previousLimit ? previousLimit + 1 : 1;

    if (ranges[index].limit === null) {
        return `A partir do ${start}`;
    }

    if (!currentLimit) {
        return "Faixa incompleta";
    }

    return `${start} ate ${currentLimit}`;
};

const CampaignsConfig = () => {
    const classes = useStyles();

    const [settings, setSettings] = useState(initialSettings);
    const [showVariablesForm, setShowVariablesForm] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [variable, setVariable] = useState({ key: "", value: "" });

    const intervalValidation = useMemo(
        () => normalizeIntervalRanges(settings.intervalRanges),
        [settings.intervalRanges]
    );

    useEffect(() => {
        api.get("/campaign-settings").then(({ data }) => {
            const settingsList = [];
            if (Array.isArray(data) && data.length > 0) {
                data.forEach((item) => {
                    settingsList.push([item.key, safeJsonParse(item.value, item.value)]);
                });

                const loadedSettings = Object.fromEntries(settingsList);
                const legacySettings = {
                    ...legacyDefaults,
                    ...loadedSettings,
                };

                const intervalRangesValidation = normalizeIntervalRanges(loadedSettings.intervalRanges);
                const intervalRanges = intervalRangesValidation.errors.length
                    ? buildLegacyIntervalRanges(legacySettings)
                    : intervalRangesValidation.ranges;

                setSettings({
                    ...initialSettings,
                    ...loadedSettings,
                    variables: Array.isArray(loadedSettings.variables) ? loadedSettings.variables : [],
                    intervalRanges,
                });
            }
        });
    }, []);

    const handleOnChangeVariable = (e) => {
        const { name, value } = e.target;
        setVariable((prev) => ({ ...prev, [name]: value }));
    };

    const updateRange = (index, field, value) => {
        setSettings((prev) => {
            const intervalRanges = [...prev.intervalRanges];
            intervalRanges[index] = {
                ...intervalRanges[index],
                [field]: value,
            };

            return {
                ...prev,
                intervalRanges: sortRangesForState(intervalRanges),
            };
        });
    };

    const addRange = () => {
        setSettings((prev) => {
            const numericLimits = prev.intervalRanges
                .map((range) => toPositiveInteger(range.limit))
                .filter(Boolean);
            const lastLimit = numericLimits.length ? Math.max(...numericLimits) : 0;
            const lastRange = prev.intervalRanges[prev.intervalRanges.length - 1] || { min: 10, max: 20 };

            return {
                ...prev,
                intervalRanges: sortRangesForState([
                    ...prev.intervalRanges,
                    {
                        limit: lastLimit + 20,
                        min: lastRange.min || 10,
                        max: lastRange.max || 20,
                    },
                ]),
            };
        });
    };

    const addOpenEndedRange = () => {
        setSettings((prev) => {
            if (prev.intervalRanges.some((range) => range.limit === null)) {
                return prev;
            }

            const lastRange = prev.intervalRanges[prev.intervalRanges.length - 1] || { min: 10, max: 20 };

            return {
                ...prev,
                intervalRanges: sortRangesForState([
                    ...prev.intervalRanges,
                    {
                        limit: null,
                        min: lastRange.min || 10,
                        max: lastRange.max || 20,
                    },
                ]),
            };
        });
    };

    const removeRange = (index) => {
        setSettings((prev) => ({
            ...prev,
            intervalRanges: prev.intervalRanges.filter((_, rangeIndex) => rangeIndex !== index),
        }));
    };

    const addVariable = () => {
        if (!variable.key || !variable.value) {
            toast.error("Informe o atalho e o conteudo da variavel.");
            return;
        }

        setSettings((prev) => {
            const variablesExists = prev.variables.some((v) => v.key === variable.key);
            if (variablesExists) {
                return prev;
            }

            setVariable({ key: "", value: "" });

            return {
                ...prev,
                variables: [...prev.variables, { ...variable }],
            };
        });
    };

    const removeVariable = () => {
        const newList = settings.variables.filter((v) => v.key !== selectedKey);
        setSettings((prev) => ({ ...prev, variables: newList }));
        setSelectedKey(null);
    };

    const saveSettings = async () => {
        const validation = normalizeIntervalRanges(settings.intervalRanges);

        if (validation.errors.length) {
            toast.error(validation.errors[0]);
            return;
        }

        const payload = {
            ...settings,
            intervalRanges: validation.ranges,
        };

        await api.post("/campaign-settings", { settings: payload });
        setSettings(payload);
        toast.success("Configuracoes salvas");
    };

    const orderedRanges = sortRangesForState(settings.intervalRanges);
    const hasOpenEndedRange = orderedRanges.some((range) => range.limit === null);

    return (
        <MainContainer>
            <ConfirmationModal
                title={i18n.t("campaigns.confirmationModal.deleteTitle")}
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={removeVariable}
            >
                {i18n.t("campaigns.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Grid style={{ width: "99.6%" }} container>
                    <Grid xs={12} item>
                        <Title>{i18n.t("campaignsConfig.title")}</Title>
                    </Grid>
                </Grid>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Box className={classes.tabPanelsContainer}>
                    <Grid spacing={2} container>
                        <Grid xs={12} item>
                            <Typography className={classes.sectionTitle} component={"h3"}>
                                Intervalos de disparo
                            </Typography>
                            <Typography className={classes.helperText} variant="body2">
                                Configure faixas em segundos. A ordem e calculada automaticamente pelo limite de
                                contatos, e a ultima faixa pode ficar sem limite.
                            </Typography>
                        </Grid>
                        <Grid xs={12} item>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Faixa</TableCell>
                                        <TableCell>Contatos</TableCell>
                                        <TableCell>Quantidade final</TableCell>
                                        <TableCell>Minimo (segundos)</TableCell>
                                        <TableCell>Maximo (segundos)</TableCell>
                                        <TableCell align="right">Acoes</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orderedRanges.map((range, index) => (
                                        <TableRow key={`${range.limit}-${index}`}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{getRangeLabel(orderedRanges, index)}</TableCell>
                                            <TableCell>
                                                {range.limit === null ? (
                                                    <Typography variant="body2">Sem limite</Typography>
                                                ) : (
                                                    <TextField
                                                        className={classes.tableInput}
                                                        variant="outlined"
                                                        size="small"
                                                        type="number"
                                                        value={range.limit}
                                                        onChange={(e) => updateRange(index, "limit", e.target.value)}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    className={classes.tableInput}
                                                    variant="outlined"
                                                    size="small"
                                                    type="number"
                                                    value={range.min}
                                                    onChange={(e) => updateRange(index, "min", e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    className={classes.tableInput}
                                                    variant="outlined"
                                                    size="small"
                                                    type="number"
                                                    value={range.max}
                                                    onChange={(e) => updateRange(index, "max", e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeRange(index)}
                                                    disabled={orderedRanges.length === 1}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {intervalValidation.errors.length > 0 && (
                                <Typography className={classes.errorText} variant="body2">
                                    {intervalValidation.errors[0]}
                                </Typography>
                            )}
                        </Grid>
                        <Grid xs={12} className={classes.textRight} item>
                            <Button onClick={addRange} color="primary" style={{ marginRight: 10 }}>
                                Adicionar faixa
                            </Button>
                            <Button
                                onClick={addOpenEndedRange}
                                color="primary"
                                style={{ marginRight: 10 }}
                                disabled={hasOpenEndedRange}
                            >
                                Adicionar faixa final
                            </Button>
                            <Button
                                onClick={() => setShowVariablesForm(!showVariablesForm)}
                                color="primary"
                                style={{ marginRight: 10 }}
                            >
                                Adicionar Variavel
                            </Button>
                            <Button
                                onClick={saveSettings}
                                color="primary"
                                variant="contained"
                                disabled={intervalValidation.errors.length > 0}
                            >
                                Salvar Configuracoes
                            </Button>
                        </Grid>
                        {showVariablesForm && (
                            <>
                                <Grid xs={12} md={6} item>
                                    <TextField
                                        label="Atalho"
                                        variant="outlined"
                                        value={variable.key}
                                        name="key"
                                        onChange={handleOnChangeVariable}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid xs={12} md={6} item>
                                    <TextField
                                        label="Conteudo"
                                        variant="outlined"
                                        value={variable.value}
                                        name="value"
                                        onChange={handleOnChangeVariable}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid xs={12} className={classes.textRight} item>
                                    <Button
                                        onClick={() => setShowVariablesForm(!showVariablesForm)}
                                        color="primary"
                                        style={{ marginRight: 10 }}
                                    >
                                        Fechar
                                    </Button>
                                    <Button onClick={addVariable} color="primary" variant="contained">
                                        Adicionar
                                    </Button>
                                </Grid>
                            </>
                        )}
                        {settings.variables.length > 0 && (
                            <Grid xs={12} className={classes.textRight} item>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ width: "1%" }}></TableCell>
                                            <TableCell>Atalho</TableCell>
                                            <TableCell>Conteudo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Array.isArray(settings.variables) &&
                                            settings.variables.map((v, k) => (
                                                <TableRow key={k}>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedKey(v.key);
                                                                setConfirmationOpen(true);
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{"{" + v.key + "}"}</TableCell>
                                                    <TableCell>{v.value}</TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Paper>
        </MainContainer>
    );
};

export default CampaignsConfig;
